import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Row, Col, Button, Table, Form, Alert } from 'react-bootstrap';
import Breadcrumbs from '../../Common/Breadcrumbs';
import ShimmerLoader from '../../Common/ShimmerLoader';
import { isSuperAdmin, isOperationsAdmin } from '../../utils/roleBasedAccess';

// Dummy data for teams (this would come from API in real implementation)
const DUMMY_TEAMS = {
  '1': [
    { id: 't1', name: 'Mumbai United', abbreviation: 'MU', currentGroup: null },
    { id: 't2', name: 'Pune Warriors', abbreviation: 'PW', currentGroup: null },
    { id: 't3', name: 'Delhi Dynamos', abbreviation: 'DD', currentGroup: 'Group A' },
    { id: 't4', name: 'Chennai Super Kings', abbreviation: 'CSK', currentGroup: 'Group A' },
    { id: 't5', name: 'Bengaluru FC', abbreviation: 'BFC', currentGroup: 'Group B' },
    { id: 't6', name: 'Kolkata Knight Riders', abbreviation: 'KKR', currentGroup: null },
    { id: 't7', name: 'Rajasthan Royals', abbreviation: 'RR', currentGroup: null }
  ],
  '2': [
    { id: 't1', name: 'Pune Tennis Club', abbreviation: 'PTC', currentGroup: null },
    { id: 't2', name: 'Mumbai Tennis Academy', abbreviation: 'MTA', currentGroup: 'Group A' },
    { id: 't3', name: 'Delhi Tennis Center', abbreviation: 'DTC', currentGroup: 'Group A' },
    { id: 't4', name: 'Chennai Tennis Club', abbreviation: 'CTC', currentGroup: 'Group B' },
    { id: 't5', name: 'Bangalore Tennis Club', abbreviation: 'BTC', currentGroup: null }
  ],
  '3': [
    { id: 't1', name: 'Pune Tennis Club', abbreviation: 'PTC', currentGroup: null },
    { id: 't2', name: 'Mumbai Tennis Academy', abbreviation: 'MTA', currentGroup: 'Group A' },
    { id: 't3', name: 'Delhi Tennis Center', abbreviation: 'DTC', currentGroup: 'Group A' },
    { id: 't4', name: 'Chennai Tennis Club', abbreviation: 'CTC', currentGroup: 'Group B' }
  ],
  '4': [
    { id: 't1', name: 'Bengaluru Pickleball Club', abbreviation: 'BPC', currentGroup: null },
    { id: 't2', name: 'Mumbai Pickleball Academy', abbreviation: 'MPA', currentGroup: 'Group A' },
    { id: 't3', name: 'Delhi Pickleball Center', abbreviation: 'DPC', currentGroup: 'Group B' },
    { id: 't4', name: 'Chennai Pickleball Club', abbreviation: 'CPC', currentGroup: 'Group B' }
  ],
  '5': [
    { id: 't1', name: 'Delhi United', abbreviation: 'DU', currentGroup: null },
    { id: 't2', name: 'Mumbai City', abbreviation: 'MC', currentGroup: 'Group A' },
    { id: 't3', name: 'Pune Warriors', abbreviation: 'PW', currentGroup: 'Group B' },
    { id: 't4', name: 'Chennai Super Kings', abbreviation: 'CSK', currentGroup: 'Group B' }
  ],
  '6': [
    { id: 't1', name: 'Chennai Super Kings', abbreviation: 'CSK', currentGroup: null },
    { id: 't2', name: 'Mumbai Indians', abbreviation: 'MI', currentGroup: 'Group A' },
    { id: 't3', name: 'Delhi Capitals', abbreviation: 'DC', currentGroup: 'Group A' }
  ]
};

// Dummy fixture data for tournament names
const DUMMY_FIXTURE_NAMES = {
  '1': 'SFL Football League Mumbai Edition',
  '2': 'SFL Tennis Championship Pune',
  '3': 'SFL Tennis Championship Pune',
  '4': 'SFL Pickleball Tournament Bengaluru',
  '5': 'SFL Football League Delhi Edition',
  '6': 'SFL Cricket Championship Chennai'
};

const AddGroupPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { fixtureId } = useParams<{ fixtureId: string }>();
  const { user, role, permissions } = useSelector((state: any) => state.Login);

  // State management
  const [groupName, setGroupName] = useState('');
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Check if user has access
  const userData = {
    ...user,
    subRole: role,
    permissions: permissions
  };

  const isSuperAdminUser = isSuperAdmin(userData);
  const isOperationsAdminUser = isOperationsAdmin(userData);

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Dashboard', onClick: () => navigate('/dashboard') },
    { label: 'Manage Tournaments', onClick: () => navigate('/fixtures') },
    { label: 'Fixture Details', onClick: () => navigate(`/fixtures/${fixtureId}`, {
      state: {
        activeTab: 'groups',
        selectedTournament: location.state?.selectedTournament,
        selectedFixture: location.state?.selectedFixture
      }
    }) },
    { label: 'Add Group', active: true }
  ];

  useEffect(() => {
    if (!isSuperAdminUser && !isOperationsAdminUser) {
      setLoading(false);
      return;
    }

    // Load teams for the fixture
    const loadTeams = () => {
      try {
        setLoading(true);
        setError(null);
        
        // Check if we have data from navigation state (from FixtureDetailsPage)
        if (location.state?.selectedTournament && location.state?.selectedFixture) {
          // Use dummy teams data for now - in real implementation, this would come from API
          const dummyTeams = [
            { id: 't1', name: 'Mumbai United', abbreviation: 'MU', currentGroup: null },
            { id: 't2', name: 'Pune Warriors', abbreviation: 'PW', currentGroup: null },
            { id: 't3', name: 'Delhi Dynamos', abbreviation: 'DD', currentGroup: 'Group A' },
            { id: 't4', name: 'Chennai Super Kings', abbreviation: 'CSK', currentGroup: 'Group A' },
            { id: 't5', name: 'Bengaluru FC', abbreviation: 'BFC', currentGroup: 'Group B' },
            { id: 't6', name: 'Kolkata Knight Riders', abbreviation: 'KKR', currentGroup: null },
            { id: 't7', name: 'Rajasthan Royals', abbreviation: 'RR', currentGroup: null }
          ];
          setTeams(dummyTeams);
          setLoading(false);
        } else {
          // Fallback to dummy data lookup for direct navigation
          setTimeout(() => {
            const fixtureTeams = DUMMY_TEAMS[fixtureId as keyof typeof DUMMY_TEAMS];
            if (fixtureTeams) {
              setTeams(fixtureTeams);
            } else {
              setError('Teams not found for this fixture');
            }
            setLoading(false);
          }, 500);
        }
      } catch (error) {
        console.error('Error loading teams:', error);
        setError('Failed to load teams');
        setLoading(false);
      }
    };

    loadTeams();
  }, [fixtureId, isSuperAdminUser, isOperationsAdminUser]);

  // Handle team selection
  const handleTeamSelection = (teamId: string, isSelected: boolean) => {
    // Find the team to check if it's already assigned
    const team = teams.find(t => t.id === teamId);
    
    // Prevent selection if team is already assigned to a group
    if (isSelected && team && team.currentGroup) {
      setError(`Cannot select ${team.name} - already assigned to ${team.currentGroup}`);
      return;
    }
    
    if (isSelected) {
      setSelectedTeams(prev => [...prev, teamId]);
    } else {
      setSelectedTeams(prev => prev.filter(id => id !== teamId));
    }
  };

  // Handle select all teams (only unassigned teams)
  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      // Only select teams that are not already assigned to a group
      const unassignedTeamIds = teams
        .filter(team => !team.currentGroup)
        .map(team => team.id);
      setSelectedTeams(unassignedTeamIds);
    } else {
      setSelectedTeams([]);
    }
  };

  // Handle save group
  const handleSaveGroup = async () => {
    if (!groupName.trim() || selectedTeams.length === 0) {
      setError('Please enter a group name and select at least one team');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      // TODO: Implement actual API call to create group
      console.log('Creating group:', {
        fixtureId,
        groupName: groupName.trim(),
        teamIds: selectedTeams
      });

      setSuccess('Group created successfully!');
      
      // Navigate back to fixture details after a short delay
      setTimeout(() => {
        navigate(`/fixtures/${fixtureId}`, { 
          state: { activeTab: 'groups', success: 'Group created successfully!' }
        });
      }, 1500);

    } catch (error) {
      console.error('Error creating group:', error);
      setError('Failed to create group. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    navigate(`/fixtures/${fixtureId}`, { 
      state: { 
        activeTab: 'groups',
        selectedTournament: location.state?.selectedTournament,
        selectedFixture: location.state?.selectedFixture
      }
    });
  };

  // Form validation
  const isFormValid = groupName.trim() && selectedTeams.length > 0;
  // Calculate if all unassigned teams are selected
  const unassignedTeams = teams.filter(team => !team.currentGroup);
  const allTeamsSelected = selectedTeams.length === unassignedTeams.length && unassignedTeams.length > 0;

  // Access denied for non-admin users
  if (!isSuperAdminUser && !isOperationsAdminUser) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <h3>Access Denied</h3>
            <p>You don't have permission to access this page.</p>
            <Button variant="primary" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          {/* Breadcrumbs */}
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs items={breadcrumbItems} />
          </div>

          {/* Header Shimmer */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '24px',
            padding: '0 15px'
          }}>
            <div className="shimmer-image" style={{ width: '40px', height: '40px', borderRadius: '8px' }}></div>
            <div className="shimmer-image" style={{ width: '60px', height: '60px', borderRadius: '12px' }}></div>
            <div style={{ flex: 1 }}>
              <div className="shimmer-text" style={{ width: '200px', height: '24px', marginBottom: '8px' }}></div>
              <div className="shimmer-badge" style={{ width: '150px', height: '28px', borderRadius: '12px' }}></div>
            </div>
            <div className="shimmer-button" style={{ width: '100px', height: '40px', borderRadius: '8px' }}></div>
          </div>

          {/* Form Shimmer */}
          <Row>
            <Col>
              <div className="tournament-table-wrapper">
                <ShimmerLoader type="form" />
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (error && !teams.length) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <h3>Error</h3>
            <p>{error}</p>
            <Button variant="primary" onClick={handleBack}>
              Go Back
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  const tournamentName = DUMMY_FIXTURE_NAMES[fixtureId as keyof typeof DUMMY_FIXTURE_NAMES] || 'Tournament';

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        <Row>
          <Col>
            <div className="tournaments-container">
              {/* Tab Content Container */}
              <div className="tab-content-container">
                {/* Header Section */}
                <div className="tournament-controls mb-4">
                  <Row className="align-items-center">
                    <Col md={8}>
                      <div className="tab-title-section">
                        <div className="d-flex align-items-center" style={{ gap: '16px' }}>
                          <button 
                            className="back-button"
                            onClick={handleBack}
                          >
                            <i className="ri-arrow-left-line"></i>
                          </button>
                          <h4 className="mb-0">Add Group - {tournamentName}</h4>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Success/Error Messages */}
                {success && (
                  <Alert variant="success" className="mb-4">
                    <i className="ri-check-line me-2"></i>
                    {success}
                  </Alert>
                )}

                {error && (
                  <Alert variant="danger" className="mb-4">
                    <i className="ri-error-warning-line me-2"></i>
                    {error}
                  </Alert>
                )}

                {/* Group Name Input */}
                <div className="mb-4">
                  <Form.Group>
                    <Form.Label className="fw-semibold">Group Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter group name"
                      value={groupName}
                      onChange={(e) => setGroupName(e.target.value)}
                      className="form-control"
                    />
                  </Form.Group>
                </div>

                {/* Teams Selection */}
                <div className="mb-4">
                  <h5 className="mb-3">Select Teams</h5>
                  <div className="alert alert-light border mb-3">
                    <i className="ri-information-line me-2 text-info"></i>
                    <small className="text-muted">
                      <strong>Note:</strong> Teams that are already assigned to a group cannot be selected for a new group. 
                      Only unassigned teams are available for selection.
                    </small>
                  </div>
                  {teams.length > 0 ? (
                    <div className="table-responsive">
                      <Table responsive className="table-hover">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '50px' }}>
                              <Form.Check
                                type="checkbox"
                                checked={allTeamsSelected}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                                label=""
                                title="Select all unassigned teams"
                              />
                            </th>
                            <th>Team Name</th>
                            <th>Abbreviation</th>
                            <th>Current Group</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teams.map((team) => {
                            const isAssigned = team.currentGroup !== null;
                            const isSelected = selectedTeams.includes(team.id);
                            
                            return (
                              <tr key={team.id} className={isAssigned ? 'opacity-75' : ''}>
                                <td>
                                  <Form.Check
                                    type="checkbox"
                                    checked={isSelected}
                                    disabled={isAssigned}
                                    onChange={(e) => handleTeamSelection(team.id, e.target.checked)}
                                    label=""
                                  />
                                </td>
                                <td className={isAssigned ? 'text-muted' : ''}>
                                  {team.name}
                                  {isAssigned && <small className="d-block text-muted opacity-75">Already assigned</small>}
                                </td>
                                <td className={isAssigned ? 'text-muted' : ''}>{team.abbreviation}</td>
                                <td>
                                  {team.currentGroup ? (
                                    <span className="badge bg-light text-dark border">{team.currentGroup}</span>
                                  ) : (
                                    <span className="text-muted">Unassigned</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-muted">No teams available for this fixture.</p>
                    </div>
                  )}
                  
                  {/* Team Summary */}
                  {teams.length > 0 && (
                    <div className="mt-3">
                      <small className="text-muted opacity-75">
                        <strong>Summary:</strong> {unassignedTeams.length} unassigned teams available, 
                        {teams.length - unassignedTeams.length} teams already assigned to groups
                      </small>
                    </div>
                  )}
                </div>

                {/* Add Group Button */}
                <div className="d-flex gap-3 justify-content-end">
                  <Button 
                    variant="outline-secondary" 
                    onClick={handleBack}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="primary" 
                    onClick={handleSaveGroup}
                    disabled={!isFormValid || saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Creating Group...
                      </>
                    ) : (
                      'Add Group'
                    )}
                  </Button>
                </div>

                {/* Selection Summary */}
                {selectedTeams.length > 0 && (
                  <div className="mt-3">
                    <small className="text-muted">
                      {selectedTeams.length} team{selectedTeams.length !== 1 ? 's' : ''} selected
                    </small>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AddGroupPage;
